import type { HangarUnitRow, UnitRow } from '@/types/hangar-units'
import type { GeneratedUnit } from '@/types/hangar-units'
import { getUnitColor } from '@/lib/utils/t-hangar-generator'

interface TRowPreviewProps {
  /** Units to render (can be saved units or generated preview) */
  units: (HangarUnitRow | GeneratedUnit)[]
  /** Template dimensions for scaling */
  template: { door_width: number; depth: number; tail_width: number }
  /** Taxilane width in feet */
  taxilaneWidth: number
  /** ID of currently selected unit */
  selectedUnitId?: string
  /** Callback when a unit is clicked */
  onUnitClick?: (unit: HangarUnitRow | GeneratedUnit) => void
  /** Whether units are interactive */
  interactive?: boolean
}

/**
 * SVG visualization of T-hangar row layout
 * Shows T-shaped units with door facing taxilane
 * Supports both single-row and nested (teeth) layouts
 */
export function TRowPreview({
  units,
  template,
  taxilaneWidth,
  selectedUnitId,
  onUnitClick,
  interactive = true,
}: TRowPreviewProps) {
  if (units.length === 0) {
    return (
      <div className="flex items-center justify-center h-40 border rounded-md bg-muted/50">
        <p className="text-sm text-muted-foreground">No units configured</p>
      </div>
    )
  }

  // Detect if this is a nested layout (units have 'row' property with 'top' value)
  const isNestedLayout = units.some((u) => 'row' in u && u.row === 'top')

  // Separate units by row for nested layout
  const bottomRowUnits = units.filter(
    (u) => !('row' in u) || (u as GeneratedUnit).row === 'bottom'
  )
  const topRowUnits = units.filter(
    (u) => 'row' in u && (u as GeneratedUnit).row === 'top'
  )

  // Calculate SVG dimensions
  const padding = 20
  const scale = 4 // 1 foot = 4 pixels for reasonable sizing

  // Get max extent of units (considering both rows for nested)
  const maxX = Math.max(
    ...units.map(
      (u) => (u.offset_x ?? 0) + (u.door_width ?? template.door_width)
    )
  )
  const unitDepth = Math.max(...units.map((u) => u.depth ?? template.depth))

  // For nested layout: bottom row + taxilane + top row
  // For single layout: units + taxilane
  const totalHeight = isNestedLayout
    ? unitDepth * 2 + taxilaneWidth
    : unitDepth + taxilaneWidth

  const svgWidth = maxX * scale + padding * 2
  const svgHeight = totalHeight * scale + padding * 2 + 25 // Extra for legend

  // T-shape path generator for bottom row (door facing down toward taxilane)
  const getBottomTShapePath = (
    x: number,
    doorWidth: number,
    depth: number,
    tailWidth: number
  ): string => {
    // T-shape: wider at door (bottom), narrower at tail (top)
    const tailOffset = (doorWidth - tailWidth) / 2
    const tailDepth = depth * 0.4 // Tail section is ~40% of total depth

    return `
      M ${x + tailOffset} 0
      L ${x + tailOffset + tailWidth} 0
      L ${x + tailOffset + tailWidth} ${tailDepth}
      L ${x + doorWidth} ${tailDepth}
      L ${x + doorWidth} ${depth}
      L ${x} ${depth}
      L ${x} ${tailDepth}
      L ${x + tailOffset} ${tailDepth}
      Z
    `
  }

  // T-shape path generator for top row (door facing up toward taxilane - flipped)
  const getTopTShapePath = (
    x: number,
    doorWidth: number,
    depth: number,
    tailWidth: number
  ): string => {
    // T-shape: wider at door (top), narrower at tail (bottom) - inverted
    const tailOffset = (doorWidth - tailWidth) / 2
    const tailDepth = depth * 0.4 // Tail section is ~40% of total depth
    const doorDepth = depth - tailDepth

    return `
      M ${x} 0
      L ${x + doorWidth} 0
      L ${x + doorWidth} ${doorDepth}
      L ${x + tailOffset + tailWidth} ${doorDepth}
      L ${x + tailOffset + tailWidth} ${depth}
      L ${x + tailOffset} ${depth}
      L ${x + tailOffset} ${doorDepth}
      L ${x} ${doorDepth}
      Z
    `
  }

  // Render a single unit
  const renderUnit = (
    unit: HangarUnitRow | GeneratedUnit,
    index: number,
    row: UnitRow,
    yOffset: number
  ) => {
    const x = (unit.offset_x ?? index * template.door_width) * scale
    const doorWidth = (unit.door_width ?? template.door_width) * scale
    const depth = (unit.depth ?? template.depth) * scale
    const tailWidth = (unit.tail_width ?? template.tail_width) * scale
    const status = 'status' in unit ? unit.status : 'available'
    const isSelected = 'id' in unit && unit.id === selectedUnitId
    const fillColor = getUnitColor(status, isSelected)

    const pathGenerator = row === 'top' ? getTopTShapePath : getBottomTShapePath
    const path = pathGenerator(x / scale, doorWidth / scale, depth / scale, tailWidth / scale)

    return (
      <g
        key={unit.unit_code}
        transform={`translate(0, ${yOffset})`}
        onClick={() => interactive && onUnitClick?.(unit)}
        className={interactive ? 'cursor-pointer hover:opacity-80' : ''}
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
      >
        {/* T-shape unit */}
        <path
          d={path}
          transform={`scale(${scale})`}
          fill={fillColor}
          stroke={isSelected ? '#7c3aed' : '#64748b'}
          strokeWidth={isSelected ? 2 : 1}
          className="transition-colors"
        />

        {/* Unit label */}
        <text
          x={x + doorWidth / 2}
          y={depth / 2}
          textAnchor="middle"
          dominantBaseline="middle"
          className="fill-white font-semibold pointer-events-none"
          style={{ fontSize: '11px' }}
        >
          {unit.unit_code}
        </text>
      </g>
    )
  }

  // Calculate taxilane Y position
  const taxilaneY = isNestedLayout
    ? padding + unitDepth * scale
    : padding + unitDepth * scale

  return (
    <div className="w-full overflow-x-auto">
      <svg
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        className="w-full max-w-full"
        style={{ minWidth: '300px', maxHeight: isNestedLayout ? '400px' : '300px' }}
      >
        {/* Background */}
        <rect
          x={0}
          y={0}
          width={svgWidth}
          height={svgHeight}
          fill="#f8fafc"
          className="stroke-slate-200"
          strokeWidth={1}
        />

        {/* Taxilane */}
        <rect
          x={padding}
          y={taxilaneY}
          width={maxX * scale}
          height={taxilaneWidth * scale}
          fill="#94a3b8"
          className="stroke-slate-400"
          strokeWidth={1}
        />
        <text
          x={padding + (maxX * scale) / 2}
          y={taxilaneY + (taxilaneWidth * scale) / 2}
          textAnchor="middle"
          dominantBaseline="middle"
          className="fill-slate-600 text-xs"
          style={{ fontSize: '10px' }}
        >
          Taxilane ({taxilaneWidth} ft)
        </text>

        {/* Bottom row units (facing down toward taxilane) */}
        <g transform={`translate(${padding}, ${padding})`}>
          {bottomRowUnits.map((unit, index) =>
            renderUnit(unit, index, 'bottom', 0)
          )}
        </g>

        {/* Top row units (facing up toward taxilane) - only for nested layout */}
        {isNestedLayout && (
          <g transform={`translate(${padding}, ${taxilaneY + taxilaneWidth * scale})`}>
            {topRowUnits.map((unit, index) =>
              renderUnit(unit, index, 'top', 0)
            )}
          </g>
        )}

        {/* Legend */}
        <g transform={`translate(${padding}, ${svgHeight - 20})`}>
          <circle cx={0} cy={0} r={5} fill="#22c55e" />
          <text x={10} y={4} className="fill-slate-600" style={{ fontSize: '9px' }}>
            Available
          </text>
          <circle cx={70} cy={0} r={5} fill="#3b82f6" />
          <text x={80} y={4} className="fill-slate-600" style={{ fontSize: '9px' }}>
            Leased
          </text>
          <circle cx={130} cy={0} r={5} fill="#f97316" />
          <text x={140} y={4} className="fill-slate-600" style={{ fontSize: '9px' }}>
            Maintenance
          </text>
        </g>
      </svg>
    </div>
  )
}
